import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { ShieldCheck, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/lib/api';
import { CONSENT_SETTINGS_EVENT, getAnalytics, observeContactSubmissions } from '@/lib/analytics';

const choiceClass = 'min-h-[44px] rounded-full border border-[#0E1B31]/25 bg-white px-5 py-3 text-sm font-semibold text-[#0E1B31] transition-colors hover:border-[#0E1B31] hover:bg-[#0E1B31] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2C4A6F]';

export default function AnalyticsConsent() {
  const controller = getAnalytics();
  const { pathname } = useLocation();
  const [consent, setConsent] = useState(() => controller.getState());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const returnFocus = useRef(null);

  useEffect(() => {
    const unsubscribe = controller.subscribe(setConsent);
    controller.start();
    const detach = observeContactSubmissions(axios, controller, API);
    const open = () => { returnFocus.current = document.activeElement; setSettingsOpen(true); };
    window.addEventListener(CONSENT_SETTINGS_EVENT, open);
    return () => {
      unsubscribe(); detach(); controller.stop();
      window.removeEventListener(CONSENT_SETTINGS_EVENT, open);
    };
  }, [controller]);

  useEffect(() => { controller.setPage(pathname); }, [controller, pathname]);

  const choose = accepted => {
    controller.choose(accepted);
    setConsent(controller.getState());
    setSettingsOpen(false);
  };

  const choices = (
    <div className="grid grid-cols-1 gap-2.5 min-[360px]:grid-cols-2">
      <button type="button" onClick={() => choose(true)} className={choiceClass} data-testid="analytics-accept">Αποδοχή</button>
      <button type="button" onClick={() => choose(false)} className={choiceClass} data-testid="analytics-reject">
        {consent.choice === 'accepted' ? 'Ανάκληση συγκατάθεσης' : 'Απόρριψη'}
      </button>
    </div>
  );

  return <>
    {consent.choice === 'unknown' && !settingsOpen && (
      <section aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-description" role="region" data-testid="cookie-banner"
        className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-3xl rounded-[24px] border border-[#0E1B31]/15 bg-[#F8F6F0] p-4 text-[#0E1B31] shadow-[0_12px_60px_-16px_rgba(14,27,49,0.28)] sm:inset-x-6 sm:bottom-6 lg:flex lg:items-center lg:gap-5 lg:px-4 lg:py-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <ShieldCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-[#5A7265]" />
            <h2 id="cookie-banner-title" className="font-display text-lg font-semibold leading-6 sm:text-xl">Σεβόμαστε την ιδιωτικότητά σας.</h2>
          </div>
          <p id="cookie-banner-description" className="mt-1 text-sm leading-[1.5] text-[#3B4A5E]">
            Χρησιμοποιούμε προαιρετικά cookies για την ανάλυση της επισκεψιμότητας και τη βελτίωση της εμπειρίας σας.
            {' '}<Link to="/cookies" className="underline underline-offset-4 hover:text-[#0E1B31]">Πολιτική Cookies</Link>
          </p>
        </div>
        <div className="mt-3 shrink-0 lg:mt-0 lg:w-[224px]">{choices}</div>
      </section>
    )}

    <Dialog.Root open={settingsOpen} onOpenChange={setSettingsOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-[#0E1B31]/45 backdrop-blur-[3px]" />
        <Dialog.Content data-testid="cookie-settings"
          onCloseAutoFocus={event => { event.preventDefault(); returnFocus.current?.focus(); }}
          className="fixed left-1/2 top-1/2 z-[81] max-h-[85dvh] w-[calc(100%_-_2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[28px] border border-[#0E1B31]/15 bg-[#F8F6F0] p-6 text-[#0E1B31] shadow-2xl sm:p-8 focus:outline-none">
          <Dialog.Title className="pr-8 font-display text-3xl font-semibold">Ρυθμίσεις cookies</Dialog.Title>
          <Dialog.Description className="mt-4 text-sm leading-relaxed text-[#3B4A5E]">
            Επιλέγετε εσείς αν θα χρησιμοποιούνται analytics. Μπορείτε να αλλάξετε την επιλογή σας οποτεδήποτε από το footer.
          </Dialog.Description>
          <div className="my-6 space-y-4 border-y border-[#0E1B31]/10 py-5 text-sm leading-relaxed">
            <p><span className="font-semibold">Απαραίτητες λειτουργίες</span><br /><span className="text-[#3B4A5E]">Παραμένουν διαθέσιμες ανεξάρτητα από την επιλογή σας.</span></p>
            <p><span className="font-semibold">Google Analytics</span><br /><span className="text-[#3B4A5E]">{consent.choice === 'accepted' ? 'Ενεργό με τη συγκατάθεσή σας.' : 'Ανενεργό. Ενεργοποιείται μόνο αν το αποδεχθείτε.'} Η ανάκληση σταματά τη μελλοντική καταγραφή και διαγράφει τα analytics cookies από αυτόν τον browser.</span></p>
            {!consent.storageAvailable && <p className="text-[#3B4A5E]">Ο browser δεν επιτρέπει αποθήκευση της επιλογής. Θα σας ζητηθεί ξανά στην επόμενη επίσκεψη.</p>}
          </div>
          {choices}
          <p className="mt-5 text-xs leading-relaxed text-[#3B4A5E]">Περισσότερα στην <Link to="/cookies" onClick={() => setSettingsOpen(false)} className="underline underline-offset-4">Πολιτική Cookies</Link> και στην <Link to="/privacy-policy" onClick={() => setSettingsOpen(false)} className="underline underline-offset-4">Πολιτική Απορρήτου</Link>.</p>
          <Dialog.Close aria-label="Κλείσιμο ρυθμίσεων" className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full text-[#3B4A5E] hover:bg-[#0E1B31]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2C4A6F]">
            <X aria-hidden="true" className="h-5 w-5" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  </>;
}
